import { useSeo } from "../lib/seo";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { burst } from "../lib/fx";
import { getDeviceId, local } from "../lib/storage";
import { useData } from "../context/DataContext";
import { PageShell, Panel } from "../components/PageShell";
import { ShareDialog } from "../components/reveal/ShareDialog";
import { groupShareLink, type SharePayload } from "../lib/share";
import { GoneCard, InvitePanel, labelOf, Participants, SeriesTracker, TallyBars, VotePanel, WinnerPanel, type GroupState } from "../components/group/GroupParts";
import { JoinCard } from "../components/group/JoinCard";
import { HostPanel } from "../components/group/HostPanel";
import { Countdown } from "../components/group/Countdown";
import { FloatingReactions, ReactionBar, type FloatingReaction } from "../components/group/Reactions";
import { GroupWheelReveal } from "../components/group/GroupWheelReveal";
import { SoundToggle } from "../components/SoundToggle";

function useGroup(groupId: string, onReaction: (r: { reaction: string; name: string }) => void) {
  const [group, setGroup] = useState<GroupState | null>(null);
  const [error, setError] = useState("");
  const [live, setLive] = useState(false);
  const stop = useRef(false);
  const reactRef = useRef(onReaction);
  reactRef.current = onReaction;
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/groups/${groupId}`, { params: { device_id: getDeviceId() } });
      setGroup(data);
      if (data.status === "finished") stop.current = true;
    } catch (e: any) {
      if ([404, 410].includes(e.response?.status)) {
        stop.current = true;
        setError(apiError(e));
      }
    }
  }, [groupId]);

  useEffect(() => {
    stop.current = false;
    let ws: WebSocket | null = null;
    let retry: ReturnType<typeof setTimeout>;
    let attempts = 0;
    let closedByUs = false;
    const wsUrl = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws")}/api/groups/${groupId}/ws`;
    const connect = () => {
      if (closedByUs) return;
      ws = new WebSocket(wsUrl);
      ws.onopen = () => { attempts = 0; setLive(true); load(); };
      ws.onmessage = (ev) => {
        let msg: any = {};
        try { msg = JSON.parse(ev.data); } catch { /* ignore */ }
        if (msg.type === "reaction") reactRef.current(msg);
        else load();
      };
      ws.onclose = () => {
        setLive(false);
        if (!closedByUs) retry = setTimeout(connect, Math.min(10000, 1000 * 2 ** attempts++));
      };
    };
    load();
    connect();
    const ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send("ping"), 25000);
    const fallback = setInterval(() => !stop.current && document.visibilityState === "visible" && load(), 15000);
    return () => {
      stop.current = true;
      closedByUs = true;
      clearTimeout(retry);
      clearInterval(ping);
      clearInterval(fallback);
      ws?.close();
    };
  }, [groupId, load]);
  return { group, setGroup, error, load, live };
}

export default function GroupRoom() {
  const { groupId = "" } = useParams();
  useSeo({ title: "Group vote | DITHER", description: "Vote by name on DITHER.", noindex: true });
  const [floating, setFloating] = useState<FloatingReaction[]>([]);
  const [revealOpen, setRevealOpen] = useState(false);
  const onReaction = useCallback((r: { reaction: string; name: string }) => {
    const item = { key: Date.now() + Math.random(), reaction: r.reaction, name: r.name, x: 8 + Math.random() * 80 };
    setFloating((f) => [...f.slice(-14), item]);
    setTimeout(() => setFloating((f) => f.filter((x) => x.key !== item.key)), 1900);
  }, []);
  const { group, setGroup, error, load, live } = useGroup(groupId, onReaction);
  const { addHistory, fastMode } = useData();
  const [busy, setBusy] = useState(false);
  const [share, setShare] = useState<SharePayload | null>(null);
  const hostToken = local.hostToken(groupId);
  const roundsSeen = useRef<number | null>(null);
  const celebrated = useRef(false);
  const prevStatus = useRef<string | null>(null);

  useEffect(() => {
    if (!group) return;
    if (roundsSeen.current !== null && group.rounds.length > roundsSeen.current && group.status === "voting") {
      const r = group.rounds[group.rounds.length - 1];
      toast.success(`Round ${r.number} → ${labelOf(group, r.winner_option_id)}${r.dither_tiebreak ? " (DITHER tiebreak)" : ""}. Vote again!`);
    }
    roundsSeen.current = group.rounds.length;
    const justFinished = prevStatus.current === "voting" && group.status === "finished";
    prevStatus.current = group.status;
    if (group.status === "finished" && !celebrated.current) {
      celebrated.current = true;
      if (justFinished) setRevealOpen(true);
      else if (!fastMode) burst();
      addHistory({ kind: "group", title: group.title, result: labelOf(group, group.winner_option_id), category: "group" });
    }
  }, [group, fastMode, addHistory]);

  const vote = async (optionId: string) => {
    if (!group?.me) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/vote`, { participant_id: group.me.id, device_id: getDeviceId(), option_id: optionId });
      setGroup(data);
    } catch (e) {
      toast.error(apiError(e));
      load();
    } finally {
      setBusy(false);
    }
  };

  if (error) return <PageShell title="Group decision" back="/" testId="group-room-page"><GoneCard message={error} /></PageShell>;
  if (!group) return <PageShell title="Loading…" testId="group-room-page"><Panel><p className="font-bold animate-pulse">Fetching the vote…</p></Panel></PageShell>;

  const voting = group.status === "voting";
  return (
    <PageShell title={group.title} subtitle={`${group.mode === "best_of_3" ? "Best of 3" : "Quick vote"} · hosted by ${group.host_name}`} testId="group-room-page">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
        {voting ? (
          <p className={`inline-flex items-center gap-2 rounded-full border-2 border-ink px-3 py-1 text-xs font-extrabold uppercase ${live ? "bg-pop-green text-white" : "bg-zinc-100"}`} data-testid="group-live-indicator" data-live={live}>
            <span className={`h-2 w-2 rounded-full ${live ? "bg-white animate-pulse" : "bg-zinc-400"}`} />{live ? "Live" : "Reconnecting…"}
          </p>
        ) : <span />}
          <SoundToggle place="group" />
        </div>
        {!voting && (
          <WinnerPanel group={group} onReveal={() => setRevealOpen(true)} onShare={() => setShare({ heading: group.title, result: labelOf(group, group.winner_option_id), context: `Decided by ${group.participants.length} people`, link: groupShareLink(group.id) })} />
        )}
        {voting && <InvitePanel group={group} />}
        <Countdown group={group} />
        <SeriesTracker group={group} />
        {voting && !group.me && <JoinCard group={group} onJoined={load} />}
        {voting && group.me && <Panel><VotePanel group={group} onVote={vote} busy={busy} /></Panel>}
        {group.me && <ReactionBar group={group} />}
        <Panel className="space-y-5">
          <Participants group={group} />
          {(group.me?.vote || !voting || hostToken) && (
            <div>
              <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-zinc-500">{voting ? "Live tally" : "Final round tally"}</p>
              <TallyBars group={group} tally={voting ? group.live_tally : group.rounds[group.rounds.length - 1]?.tally || {}} />
            </div>
          )}
        </Panel>
        {voting && hostToken && <HostPanel group={group} token={hostToken} onChanged={load} />}
      </div>
      <ShareDialog payload={share} onClose={() => setShare(null)} />
      <FloatingReactions items={floating} />
      <GroupWheelReveal group={group} open={revealOpen && !voting} onClose={() => setRevealOpen(false)} fast={fastMode} hostToken={hostToken} onChanged={load} />
    </PageShell>
  );
}
